/**
 * Recent-deletions queue — temporal-correlation fallback for the
 * DOM-id mismatch.
 *
 * The problem
 * -----------
 * WhatsApp Web's revoked-message placeholder row in the DOM carries a
 * `data-id` that is COMPLETELY DIFFERENT from the original message's
 * `id._serialized`. So when the injection scans the DOM and finds a
 * `data-id="3EB084..."`, looking that up in the vault returns nothing
 * — because the vault was keyed on the ORIGINAL id (e.g. `3EB067...`).
 *
 * The solution
 * ------------
 * The deletion webhook (`message_deleted_revoked_webhook`) IS handed
 * the original `messageId` by the page-side payload builder. That's
 * the id we have in the vault. We can:
 *
 *   1. When the deletion webhook fires → look up the body in vault →
 *      push `{ msgId, body, ... }` into this queue with a TTL.
 *   2. When the injection finds a placeholder it can't match by id →
 *      ask this queue for the most-recently-pushed unconsumed entry.
 *   3. Inject the chip using that entry's body.
 *
 * It's a temporal correlation: revocations fire just before placeholders
 * render. As long as the user isn't deleting 10 messages in a single
 * frame, the order matches.
 *
 * The queue lives in module scope (one per content-script load) and is
 * deliberately NOT persisted — it's a tiny correlation buffer, not a
 * source of truth. The vault remains the source of truth for the body.
 */
import type { BackedUpMessage } from './types';

/** What we know about a deletion when it happens. */
export interface RecentDeletion {
  /** Original WA-stable msgId — for de-duplication. */
  msgId: string;
  /** The captured body we'll display when the chip is clicked. */
  body: string;
  /** Media-message caption (see BackedUpMessage.caption). */
  caption?: string;
  /** Voice-note audio (see BackedUpMessage.audioBase64). */
  audioBase64?: string;
  audioMimetype?: string;
  /** Carried through so the popup can show "this also had a picture". */
  hadMedia: boolean;
  /** Display name we resolved at capture time. */
  authorName: string;
  /** When WA reports the original was sent. */
  whatsappTimestamp: number;
  /** When the vault first captured the body. */
  capturedAt: number;
  /** When this entry was pushed onto the queue. Drives TTL. */
  pushedAt: number;
}

/** Entries older than this are ignored on consumption. Generous so a
 *  user who deletes a message in one chat and switches to view another
 *  chat before scrolling back doesn't lose the chip. 5 minutes is
 *  long enough to cover normal multi-tasking, short enough that
 *  stale entries don't pile up. */
const TTL_MS = 5 * 60_000;

/** In-memory FIFO. Newest at the END. */
const queue: RecentDeletion[] = [];

/** Drop entries past the TTL. Cheap to call before every push/peek. */
const prune = (): void => {
  const cutoff = Date.now() - TTL_MS;
  while (queue.length && queue[0].pushedAt < cutoff) queue.shift();
};

/** Drop an entry whose msgId matches — used when a chip is injected for
 *  this same entry to keep the FIFO ordering meaningful. */
const removeByMsgId = (msgId: string): void => {
  const idx = queue.findIndex((d) => d.msgId === msgId);
  if (idx >= 0) queue.splice(idx, 1);
};

/** Add an entry to the queue. Idempotent on msgId — re-pushing the same
 *  deletion (e.g. duplicate webhook delivery) just refreshes pushedAt. */
export const pushRecentDeletion = (
  entry: Omit<RecentDeletion, 'pushedAt'>,
): void => {
  prune();
  removeByMsgId(entry.msgId);
  queue.push({ ...entry, pushedAt: Date.now() });
  console.log(
    '[bt-vault] queued recent deletion',
    entry.msgId,
    `(queue size: ${queue.length})`,
  );
};

/** Convenience: push directly from a vault row. */
export const pushDeletionFromVaultRow = (row: BackedUpMessage): void => {
  pushRecentDeletion({
    msgId: row.msgId,
    body: row.body,
    caption: row.caption,
    audioBase64: row.audioBase64,
    audioMimetype: row.audioMimetype,
    hadMedia: row.hadMedia,
    authorName: row.authorName,
    whatsappTimestamp: row.whatsappTimestamp,
    capturedAt: row.capturedAt,
  });
};

/** Take the OLDEST entry off the queue and return it. Used by the
 *  injection when it finds a DOM placeholder and needs a body. FIFO
 *  matches the order of deletion events to the order placeholders
 *  render. */
export const consumeOldestDeletion = (): RecentDeletion | null => {
  prune();
  const entry = queue.shift() ?? null;
  if (entry) {
    console.log(
      '[bt-vault] consumed recent deletion',
      entry.msgId,
      `(${queue.length} left in queue)`,
    );
  }
  return entry;
};

/** Read-only peek for diagnostics. */
export const peekRecentDeletions = (): ReadonlyArray<RecentDeletion> => {
  prune();
  return queue.slice();
};

/** Counter for diagnostics. */
export const countRecentDeletions = (): number => {
  prune();
  return queue.length;
};

/** Clear everything — used by tests + wipe action. */
export const clearRecentDeletions = (): void => {
  queue.length = 0;
};
